import { useEffect, useState } from "react";
import { Footer } from "@/components/landing/Footer";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Terminal, Cpu, ArrowRight, Send, Instagram, Twitter, Globe, Youtube, ArrowUpRight } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { Credentials } from "@/components/landing/Credentials";
import { EBOOKS } from "@/components/landing/data";

const STATS = [
  { v: "18+ mo", k: "In development" },
  { v: "800+", k: "Assets scanned" },
  { v: "24/7", k: "Continuous scan" },
  { v: "<20s", k: "Alert latency" },
  { v: "16", k: "Structures detected" },
  { v: "3", k: "Exchanges wired" },
];

const TIMELINE = [
  { t: "The itch", d: "Years on the trading side of the screen — equities, index futures, crypto — and one recurring frustration: every retail tool sold hype, none showed its own numbers." },
  { t: "First engine", d: "We wrote the first pattern-detection script and spent months making a single structure fire reliably on live markets. Backtests lie; live data humbles you fast." },
  { t: "Quant rebuild", d: "We brought in quantitative researchers to replace guesswork with method — RANSAC-style geometric fitting for the shapes, statistical filters to kill false positives." },
  { t: "Verify everything", d: "We built the ledger before the marketing. Every claim timestamped, every loss published next to every win. If we can't verify it, it doesn't ship." },
  { t: "Free where they charge", d: "We opened the whale-alert and pattern channels to everyone, free, and put the full terminal on a founder waitlist." },
];

const PRINCIPLES = [
  { t: "Evidence over hype", d: "Every stat on this site is measured on our own data or cited to a source. No borrowed averages dressed up as a guarantee." },
  { t: "Publish the losses", d: "A track record that only shows winners isn't a track record. Ours shows both." },
  { t: "Built for retail", d: "Prop-grade discipline — position sizing, drawdown limits, execution under pressure — baked into a tool a retail trader can run in minutes." },
];

export default function About() {
  const [cursor, setCursor] = useState(true);
  useSeo({
    title: "About TradrQuant — Built by traders, verified by quants",
    description:
      "TradrQuant was built by a working trader and a small team of quantitative researchers and engineers to give retail traders a professional, verifiable edge. This is our story.",
    path: "/about",
  });
  useEffect(() => {
    const iv = setInterval(() => setCursor((c) => !c), 530);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div data-testid="about-page" className="grain relative min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto group-hover:scale-105 transition-transform duration-300" />
            <span data-testid="wordmark" className="tq-wordmark text-[#ECEBE4] text-[19px] sm:text-[21px]">Tradr<span className="tq-q">Q</span>uant</span>
          </Link>
          <Link
            to="/"
            data-testid="about-back-btn"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 border border-white/15 px-3 py-2 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors duration-300"
          >
            <ChevronLeft size={14} /> cd ~/terminal
          </Link>
        </div>
      </header>

      {/* hero */}
      <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-10 md:pt-16">
        <div className="relative border border-white/12 bg-[#13151A]">
          <div className="flex items-center gap-2 border-b border-white/10 bg-[#13151A] px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#BACD1C]/60" />
            <span className="ml-3 font-mono text-xs text-white/45 tracking-[0.15em] lowercase">about/tradrquant.md</span>
            <span className="ml-auto flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7D8390]">
              <Terminal size={13} /> whoami
            </span>
          </div>
          <div className="p-6 md:p-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">// our story</p>
            <h1 className="mt-4 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl lg:text-4xl leading-[1.05]">
              Built by traders.
              <span className="text-[#7D8390]"> Verified by quants.</span>
              <span className="text-[#7D8390]">{cursor ? "_" : "\u00A0"}</span>
            </h1>
            <p className="mt-5 max-w-2xl text-[13px] sm:text-sm text-white/55 leading-relaxed">
              TradrQuant started as one trader's frustration with a retail-tools market built on
              marketing instead of evidence. It became an 18-month build with a small team of
              quantitative researchers and engineers — a professional terminal that shows its own
              numbers, wins and losses alike.
            </p>
          </div>
        </div>
      </div>

      {/* stats */}
      <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-10">
        <div data-testid="about-stats" className="grid grid-cols-2 md:grid-cols-6 border border-white/12 bg-[#13151A] divide-x divide-white/10">
          {STATS.map((s, i) => (
            <div key={i} className={`p-5 ${i >= 2 ? "border-t md:border-t-0 border-white/10" : ""} ${i >= 4 ? "border-t md:border-t-0" : ""}`}>
              <div className="font-display font-semibold text-[#ECEBE4] text-2xl sm:text-3xl tracking-[-0.03em] tabular-nums">{s.v}</div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 leading-snug">{s.k}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-12 md:py-16 space-y-14">
        {/* who's behind it */}
        <section data-testid="about-founder">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">[00] // who.built_this()</p>
          <h2 className="mt-3 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl">Who's behind it</h2>
          <div className="mt-6 grid md:grid-cols-[180px_1fr] gap-6 md:gap-10 items-start">
            <div className="relative shrink-0">
              <div className="relative aspect-square w-full max-w-[180px] border border-white/15 overflow-hidden bg-[#13151A]">
                <img
                  src="/jc-rod.png"
                  alt="JC Rod — founder of TradrQuant"
                  data-testid="founder-photo"
                  className="h-full w-full object-cover"
                />
                <span className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#7D8390]">JC Rod · @Tradernaut</span>
              </div>
              <div className="mt-3 flex items-center gap-2" data-testid="founder-socials">
                <a href="https://x.com/MrTradernaut" target="_blank" rel="noopener noreferrer" data-testid="founder-x" aria-label="X (Twitter)" className="inline-flex h-9 w-9 items-center justify-center border border-white/15 text-white/60 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors"><Twitter size={15} /></a>
                <a href="https://www.instagram.com/mr.tradernaut/" target="_blank" rel="noopener noreferrer" data-testid="founder-instagram" aria-label="Instagram" className="inline-flex h-9 w-9 items-center justify-center border border-white/15 text-white/60 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors"><Instagram size={15} /></a>
                <a href="https://www.mrtradernaut.com" target="_blank" rel="noopener noreferrer" data-testid="founder-website" aria-label="Website" className="inline-flex h-9 w-9 items-center justify-center border border-white/15 text-white/60 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors"><Globe size={15} /></a>
                <a href="https://www.youtube.com/@MrTradernaut" target="_blank" rel="noopener noreferrer" data-testid="founder-youtube" aria-label="YouTube" className="inline-flex h-9 w-9 items-center justify-center border border-white/15 text-white/60 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors"><Youtube size={15} /></a>
                <a href="https://t.me/Tradernaut" target="_blank" rel="noopener noreferrer" data-testid="founder-telegram" aria-label="Telegram" className="inline-flex h-9 w-9 items-center justify-center border border-white/15 text-white/60 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors"><Send size={15} /></a>
              </div>
            </div>
            <div className="space-y-4 text-[13px] sm:text-sm text-white/60 leading-relaxed">
              <p>
                I'm <span className="text-white">JC Rod</span> — better known as <span className="text-white">Tradernaut</span>,
                founder of TradrQuant. I've spent years on the trading side of the screen across
                equities, index futures and crypto — and passed funded trading challenges with
                proprietary firms like Topstep, trading real capital under strict, rules-based risk
                parameters.
              </p>
              <p>
                That discipline — position sizing, drawdown limits, execution under pressure — plus a
                deep grounding in order flow, liquidity and market structure, is exactly what I wanted
                encoded into a tool. I've also published two trading titles teaching the craft and
                build in public with the MoonBros community.
              </p>
            </div>
          </div>

          {/* trading credentials (moved from home) */}
          <div className="mt-8">
            <Credentials />
          </div>

          {/* published work (moved from home) */}
          <div className="mt-10 pt-8 border-t border-white/10" data-testid="about-books">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/30 mb-5">Published work</p>
            <div className="flex flex-wrap gap-8">
              {EBOOKS.map((b, i) => (
                <a key={b.title} href={b.url} target="_blank" rel="noopener noreferrer" data-testid={`ebook-${i}`} className="group flex items-center gap-4">
                  <div className="relative h-20 w-[72px] shrink-0 overflow-hidden border border-white/10 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.9)] transition-transform duration-500 group-hover:-translate-y-1">
                    <img src={b.cover} alt={b.title} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <span className="max-w-[130px] font-mono text-[12px] text-white/60 leading-snug group-hover:text-white transition-colors">
                    {b.title}
                    <ArrowUpRight size={12} className="inline ml-1 mb-0.5 text-white/30 group-hover:text-[#BACD1C] transition-colors" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* the team */}
        <section data-testid="about-team">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">[01] // the.team()</p>
          <h2 className="mt-3 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl">The team</h2>
          <p className="mt-5 max-w-2xl text-[13px] sm:text-sm text-white/60 leading-relaxed">
            TradrQuant isn't a solo project. I handle the trading logic — every pattern, parameter and
            edge case comes from years of watching charts. Alongside me is a small team of{" "}
            <span className="text-white">quantitative researchers and engineers</span> who turn that
            intuition into method: geometric curve-fitting for the shapes, statistical filters to kill
            false positives, and infrastructure built to scan hundreds of pairs around the clock.
          </p>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[
              { r: "Trading logic", n: "JC Rod", d: "Founder · trader · @Tradernaut" },
              { r: "Quant research", n: "Quant team", d: "Statistical modelling & pattern validation" },
              { r: "Engineering", n: "Eng team", d: "Real-time data pipelines & 24/7 scanning" },
            ].map((m) => (
              <div key={m.r} className="border border-white/12 bg-[#13151A] p-5">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7D8390]">
                  <Cpu size={12} /> {m.r}
                </div>
                <p className="mt-3 font-display font-semibold tracking-[-0.03em] text-base">{m.n}</p>
                <p className="mt-1 font-mono text-[12px] text-white/45">{m.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* how we built it — timeline */}
        <section data-testid="about-timeline">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">[02] // how.we_built_it()</p>
          <h2 className="mt-3 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl">What it took</h2>
          <div className="mt-6 border-l border-white/10 pl-6 space-y-7">
            {TIMELINE.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="relative"
              >
                <span className="absolute -left-[29px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#BACD1C]" />
                <p className="font-display font-semibold tracking-[-0.03em] text-base text-white">{s.t}</p>
                <p className="mt-2 text-[13px] text-white/55 leading-relaxed max-w-2xl">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* principles */}
        <section data-testid="about-principles">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">[03] // what.we_stand_for()</p>
          <h2 className="mt-3 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl">What we stand for</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {PRINCIPLES.map((p) => (
              <div key={p.t} className="border border-white/12 bg-[#13151A] p-6">
                <h3 className="font-display font-semibold tracking-[-0.03em] text-lg leading-snug">{p.t}</h3>
                <p className="mt-3 text-[13px] text-white/55 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* honest take */}
        <section data-testid="about-honest" className="border-l-2 border-white/[0.10] bg-white/[0.03] pl-5 pr-4 py-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#7D8390]">// honest take</p>
          <p className="mt-3 max-w-2xl text-[13px] sm:text-sm text-white/65 leading-relaxed">
            TradrQuant scans markets, detects structures and sends you alerts with their measured
            odds. That's it. It won't make you a better trader overnight, and it won't guarantee
            profits — markets are unpredictable and no software changes that. What it does is save you
            time and give you honest numbers to decide with. We built it because we needed it. We're
            sharing it because other traders might too.
          </p>
        </section>

        {/* CTA — send users to the home page channels so they can pick */}
        <div className="relative border border-white/[0.10] bg-white/[0.03] p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-display font-semibold tracking-[-0.03em] text-lg">Trade with us — free</p>
            <p className="mt-1 font-mono text-[13px] text-white/55">Pick the free Telegram channels you want — whale alerts, pattern edge, or both.</p>
          </div>
          <Link
            to="/#free"
            data-testid="about-cta"
            className="inline-flex items-center justify-center gap-2 bg-[#BACD1C] text-black font-mono text-xs uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-white active:scale-[0.98] transition-[background-color,transform] duration-300"
          >
            <Send size={15} /> Choose your channels
          </Link>
        </div>

        <div className="flex flex-wrap gap-4 font-mono text-xs">
          <Link to="/learn" className="text-[#BACD1C]/70 hover:text-[#BACD1C] underline underline-offset-2">Read the Academy →</Link>
          <a href="https://verify.tradrquant.com" target="_blank" rel="noopener noreferrer" className="text-[#BACD1C]/70 hover:text-[#BACD1C] underline underline-offset-2">See the verified track record →</a>
          <Link to="/" className="text-white/40 hover:text-white underline underline-offset-2">Back to terminal →</Link>
        </div>
        <p className="font-mono text-[11px] text-white/25 leading-relaxed">
          Not financial advice. Trading involves substantial risk. Team composition described reflects
          TradrQuant's trading, research and engineering functions.
        </p>
      </div>
      <Footer />
    </div>
  );
}
